import type { AuthAdapter, AuthSession, TenantPlacement } from './types';
import type { RawGrant } from '../permissions/types';

/**
 * OIDC authorization code + PKCE, against whichever issuer the environment names.
 *
 * ONE adapter, not one per provider: locally the issuer is Keycloak, in EKS it is Cognito, and
 * the console is not told which (decisions T-3/T-4). Everything provider-specific — where the
 * authorize and token endpoints live, where a logout goes — is read from the issuer's discovery
 * document, so pointing `issuer` somewhere else is the entire migration.
 *
 * The SPA holds no secrets and NO REFRESH TOKEN (decision T-42, SDD-019). The browser signs in on
 * the issuer's own page (authorization code + PKCE, endpoints from discovery); the one-time code
 * and its verifier are handed to authservice (`session:exchange`), which redeems them and keeps
 * the refresh token in the HttpOnly cookie `refresh.token.<key>` — out of reach of every script
 * on this page. This adapter holds the access token and the account's username in memory, and
 * nothing else: nothing in localStorage, nothing in sessionStorage except the PKCE verifier and
 * the CSRF state, for exactly one redirect round trip (S-7).
 *
 * - A reload has empty memory, so `restore()` asks authservice to refresh WITHOUT a username: the
 *   browser's single account cookie names the account (S-7). No cookie, a dead one, or several
 *   accounts → no session, and the sign-in redirect follows (the issuer's SSO session signs the
 *   user straight back in).
 * - Refreshes are serialized PER BROWSER with the Web Locks API (S-5): rotation is on, and two
 *   tabs refreshing with one cookie would spend it twice.
 * - The SDK's `sessionRefresher` and `onSessionLost` come from here (S-6).
 */
export interface OidcConfig {
  /** e.g. `http://localhost:8080/realms/eis` — the `iss` every token carries. */
  readonly issuer: string;
  readonly clientId: string;
  readonly redirectUri: string;
  readonly scopes: readonly string[];
  /**
   * authservice's base URL — the session endpoints live under it (`<apiBaseUrl>/auth`, same origin
   * through the reverse proxy). The paths themselves come from the generated SDK (T-7).
   */
  readonly sessionBaseUrl: string;
  /**
   * The Web Locks seam (S-5). Default `navigator.locks`; `null` forces the per-tab fallback.
   * Tests pass a fake LockManager.
   */
  readonly locks?: Pick<LockManager, 'request'> | null;
}

/**
 * Everything this adapter remembers between calls. Deliberately has no field for a refresh token:
 * there is nowhere to put one (SDD-019 S-7, asserted by the adapter tests).
 */
export interface OidcSessionState {
  readonly session: AuthSession | null;
  /** The account, as the session envelope names it — sent on every refresh after the first. */
  readonly username: string | null;
  /** Only ever the issuer logout's `id_token_hint`. */
  readonly idToken: string | null;
}

/**
 * The OIDC adapter, plus a read-only view of its memory for tests. Built by `createOidcAuthAdapter`
 * in `./oidc-session` — a LAZY entry (`@eis/core/auth/oidc-session`): only a console configured
 * with `provider: "oidc"` downloads it and the session API it drives.
 */
export interface OidcAuthAdapter extends AuthAdapter {
  snapshot: () => OidcSessionState;
}

/** The claim contract, byte-for-byte as docs/standards/jwt-contract.md defines it. */
export interface JwtClaims {
  readonly sub?: string;
  readonly email?: string;
  readonly name?: string;
  readonly preferred_username?: string;
  readonly given_name?: string;
  readonly family_name?: string;
  readonly 'custom:role'?: string;
  readonly 'custom:tenant_id'?: string;
  readonly 'custom:tenant_slug'?: string;
  /** Sent by the legacy admin's issuer (its `parseTokenResponse` reads it); Keycloak does not. */
  readonly 'custom:tenant_name'?: string;
  readonly 'custom:tenant_type'?: string;
  readonly 'custom:tenant_location'?: string;
  /**
   * The caller's grants, as the issuer stamps them (decision T-14, PROVISIONAL).
   *
   * An OAuth token endpoint returns a token, not an application's authorization model, so a
   * real issuer has nowhere to put grants except INSIDE the token. The legacy admin read them
   * from the response envelope; Keycloak and Cognito both put them in a claim. Both shapes are
   * accepted — see `parseTokenResponse`.
   *
   * The claim NAME is provisional: T-4 says the contract mimics prod Cognito byte-for-byte and
   * P-1 (the redacted prod token that would pin it) is still open. `custom:permissions` follows
   * the convention the three tenancy claims already use, and is the one thing to change when
   * P-1 lands.
   */
  readonly 'custom:permissions'?: readonly string[] | string;
  readonly exp?: number;
}

interface TokenResponse {
  readonly access_token: string;
  readonly expires_in: number;
  /**
   * The permission array arrives next to the token, exactly as
   * `Services/api/auth/base.ts::parseTokenResponse` reads it in the legacy admin (Addendum,
   * Part C) — so roles behave identically in both consoles throughout the transition.
   *
   * A standards-compliant issuer never sends this: the OAuth token response is a fixed envelope.
   * It is kept because the legacy admin's own endpoint does, and the transition needs both.
   */
  readonly permissions?: readonly RawGrant[];
}

/**
 * The Web Locks names (S-5). A refresh that names its account takes `…:*` SHARED and its own
 * account's lock EXCLUSIVE; a refresh that names no account (a reload) does not know which cookie
 * the browser will send, so it takes `…:*` EXCLUSIVE and waits for every other refresh in the
 * browser. Two accounts in one browser therefore never wait for each other, and a reload never
 * races a refresh of the account it is about to learn.
 */
export const SESSION_LOCK_PREFIX = 'eis-session-refresh:';

/** The legacy cookie key (`getTokenCookieKey` minus its prefix): `ops@tenant-a` → `opstenanta`. */
export function accountKey(username: string): string {
  return username.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

export function decodeJwtClaims(token: string): JwtClaims {
  const payload = token.split('.')[1];
  if (payload === undefined) throw new Error('Malformed JWT: no payload segment.');
  const normalized = payload.replaceAll('-', '+').replaceAll('_', '/');
  const json = atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='));
  return JSON.parse(json) as JwtClaims;
}

/** Anything that is not one of the two known values is `unknown`, never a guessed default. */
export function readPlacement(claim: string | undefined): TenantPlacement {
  const normalized = claim?.toUpperCase();
  return normalized === 'POOL' || normalized === 'SILO' ? normalized : 'unknown';
}

/** The wire contract, isolated and testable: token response + JWT claims → session. */
/**
 * Grants from wherever this issuer puts them, and NEVER a default.
 *
 * Two shapes, because there are two issuers during the transition: the legacy admin's endpoint
 * puts the array beside the token, a real OIDC provider can only put it inside one. The response
 * envelope wins when both are present — it is the more specific answer, and it is what the legacy
 * console is already being served.
 *
 * A single string is accepted because a Keycloak attribute mapper with `multivalued: false`
 * emits one, and an operator who sets exactly one grant should not silently get none.
 *
 * Anything unrecognised yields `[]`, which the permission engine turns into "no products
 * assigned". That is the only safe direction to fail: a token whose authorization claim we
 * cannot read must never look like a token that granted something.
 */
function readGrants(response: TokenResponse, claims: JwtClaims): readonly RawGrant[] {
  if (response.permissions !== undefined) return response.permissions;

  const claimed = claims['custom:permissions'];
  if (typeof claimed === 'string') return claimed === '' ? [] : [claimed];
  if (Array.isArray(claimed)) {
    return claimed.filter((grant): grant is RawGrant => typeof grant === 'string' && grant !== '');
  }
  return [];
}

export function parseTokenResponse(response: TokenResponse): AuthSession {
  const claims = decodeJwtClaims(response.access_token);
  const tenantId = claims['custom:tenant_id'] ?? 'unknown';
  const slug = claims['custom:tenant_slug'] ?? tenantId;
  return {
    user: {
      id: claims.sub ?? 'unknown',
      email: claims.email ?? '',
      displayName: claims.name ?? claims.preferred_username ?? claims.email ?? 'Unknown user',
      role: claims['custom:role'] ?? 'unknown',
    },
    tenant: {
      id: tenantId,
      slug,
      name: slug,
      placement: readPlacement(claims['custom:tenant_type']),
      location: claims['custom:tenant_location'] ?? null,
    },
    grants: readGrants(response, claims),
    accessToken: response.access_token,
    expiresAt: Date.now() + response.expires_in * 1000,
  };
}

// ---------------------------------------------------------------------------------------------
// PKCE
// ---------------------------------------------------------------------------------------------

function base64url(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = '';
  for (const byte of view) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
}

export async function codeChallengeOf(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(digest);
}
